import { Component, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { DistancePipe } from '../../pipes/distance.pipe';
import { SupabaseService } from '../../services/supabase.service';
import { RaceService } from '../../services/race.service';
import { Event as RaceEvent } from '../../models/data.models';
import { getFfslcRegionForAdministrativeRegion } from '../../data/ffslc-regions';
import { BrandedLoaderComponent } from '../../components/branded-loader/branded-loader.component';
import { StyledSelectComponent } from '../../components/styled-select/styled-select.component';
import { AuthService } from '../../services/auth.service';
import { ClubService } from '../../services/club.service';
import { ImageUploadService } from '../../services/image-upload.service';
import { TranslationService } from '../../services/translation.service';
import { currentSeasonStartYear, seasonLabel, seasonStartDate, seasonStartYear } from '../../utils/season';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';

type ClubStatsRow = Record<string, unknown>;
const INDEPENDENTS = 'Indépendants';

interface ClubStats {
  id?: string;
  name: string;
  /** The row grouping participants without a club; its displayed name is translated. */
  isIndependents: boolean;
  region: string;
  logoUrl?: string;
  backgroundImage: string;
  cardColor: string;
  memberCount: number;
  activeMemberCount: number;
  totalDistance: number;
  totalRaces: number;
  podiums: {
    gold: number;
    silver: number;
    bronze: number;
    chocolate: number;
    total: number;
  };
  topSpeed: number;
}

interface ClubDirectoryEntry {
  id: string;
  name: string;
  region?: string;
  logoUrl?: string;
}

@Component({
  selector: 'app-clubs',
  standalone: true,
  imports: [CommonModule, TranslatePipe, DistancePipe, RouterModule, BrandedLoaderComponent, StyledSelectComponent, PageHeaderComponent],
  templateUrl: './clubs.component.html',
})
export class ClubsComponent {
  readonly authService = inject(AuthService);
  private clubService = inject(ClubService);
  private imageUpload = inject(ImageUploadService);
  uploadingClubId = signal<string | null>(null);
  logoFeedback = signal<{ name: string; key: string; error: boolean } | null>(null);
  private supabase = inject(SupabaseService);
  private raceService = inject(RaceService);
  private readonly clubBackgroundImages = [
    '/assets/club-background-forest.webp',
    '/assets/club-background-coast.webp',
    '/assets/club-background-mountain.webp',
    '/assets/club-background-riverside.webp',
  ];

  clubStats = signal<ClubStats[]>([]);
  private rawClubStats = signal<ClubStatsRow[]>([]);
  private seasonClubStats = signal<ClubStatsRow[]>([]);
  private allStatsLoaded = signal(false);
  private clubDirectory = signal<ClubDirectoryEntry[]>([]);
  private seasonStatsCache = new Map<number, ClubStatsRow[]>();
  /** The latest season request wins when the visitor switches seasons quickly. */
  private seasonRequest = 0;
  private publishedEvents = signal<RaceEvent[]>([]);
  isLoading = signal(true);
  searchQuery = signal('');
  sortBy = signal<'alphabet' | 'podiums' | 'distance' | 'races' | 'members'>('members');
  sortOptions = computed(() => ['alphabet', 'podiums', 'distance', 'races', 'members'].map(value => ({
    value,
    label: this.translationService.translate(`clubsKiosk.sort.${value}`),
  })));
  private translationService = inject(TranslationService);
  statsScope = signal<'season' | 'all'>('season');
  selectedSeasonStart = signal(currentSeasonStartYear());

  availableSeasons = computed(() => {
    const seasons = new Set<number>([currentSeasonStartYear(), this.selectedSeasonStart()]);
    this.publishedEvents().forEach(event => {
      const season = seasonStartYear(event.date);
      if (season !== null) seasons.add(season);
    });
    return Array.from(seasons).sort((a, b) => b - a);
  });

  selectedSeasonLabel = computed(() => seasonLabel(this.selectedSeasonStart()));

  seasonOptions = computed(() => this.availableSeasons().map(year => ({
    value: String(year),
    label: seasonLabel(year),
  })));

  filteredClubs = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const stats = this.clubStats();
    const key = this.sortBy();
    const score = (club: ClubStats) => key === 'distance' ? club.totalDistance
      : key === 'races' ? club.totalRaces : key === 'members' ? club.activeMemberCount : club.podiums.total;
    return stats.filter(c => !query || c.name.toLowerCase().includes(query))
      .sort((a, b) => (key === 'alphabet' ? 0 : score(b) - score(a)) || a.name.localeCompare(b.name, this.translationService.currentLang(), { sensitivity: 'base' }));
  });

  constructor() {
    this.loadClubs();
  }

  onSearch(event: Event) {
    const target = event.target as HTMLInputElement;
    this.searchQuery.set(target.value);
  }

  setSort(value: string) {
    if (value === 'alphabet' || value === 'podiums' || value === 'distance' || value === 'races' || value === 'members') this.sortBy.set(value);
  }

  async onLogoSelected(event: Event, club: ClubStats): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!this.authService.isAdmin() || !club.id || !file || this.uploadingClubId()) return;
    this.logoFeedback.set(null);
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 2 * 1024 * 1024) {
      this.logoFeedback.set({ name: club.name, key: 'clubsKiosk.logoInvalid', error: true });
      return;
    }
    this.uploadingClubId.set(club.id);
    try {
      const logoUrl = await this.imageUpload.uploadClubLogo(club.id, file);
      await this.clubService.updateClubLogo(club.id, logoUrl);
      void this.imageUpload.removeStoredImage(club.logoUrl);
      this.clubDirectory.update(clubs => clubs.map(entry => entry.id === club.id ? { ...entry, logoUrl } : entry));
      this.rebuildClubStats();
      this.logoFeedback.set({ name: club.name, key: 'clubsKiosk.logoSaved', error: false });
    } catch (error) {
      console.error('Error updating club logo:', error);
      this.logoFeedback.set({ name: club.name, key: 'clubsKiosk.logoError', error: true });
    } finally {
      this.uploadingClubId.set(null);
    }
  }

  async loadClubs() {
    try {
      this.isLoading.set(true);
      const currentSeason = currentSeasonStartYear();
      const [seasonRows, clubs, events] = await Promise.all([
        this.fetchSeasonStats(currentSeason),
        this.loadClubDirectory(),
        this.raceService.getPublishedEvents({ includeRaces: false }),
      ]);
      this.clubDirectory.set(clubs);
      this.publishedEvents.set(events);

      // A new season starts without results: show the latest season that has some instead.
      let season = currentSeason;
      let rows = seasonRows;
      if (!rows.some(row => Number(row['total_races']) > 0)) {
        const previousSeasons = events
          .map(event => seasonStartYear(event.date))
          .filter((year): year is number => year !== null && year < currentSeason);
        if (previousSeasons.length > 0) {
          season = Math.max(...previousSeasons);
          rows = await this.fetchSeasonStats(season);
        }
      }
      this.selectedSeasonStart.set(season);
      this.seasonClubStats.set(rows);
      this.rebuildClubStats();
    } catch (e) {
      console.error('Error loading clubs data', e);
    } finally {
      this.isLoading.set(false);
    }
  }

  async setStatsScope(scope: 'season' | 'all'): Promise<void> {
    this.statsScope.set(scope);
    if (scope === 'all' && !this.allStatsLoaded()) {
      await this.loadAllStats();
      return;
    }
    this.rebuildClubStats();
  }

  async setSeasonStart(value: string): Promise<void> {
    const seasonStart = Number(value);
    if (!Number.isInteger(seasonStart)) return;

    this.selectedSeasonStart.set(seasonStart);
    this.statsScope.set('season');
    const request = ++this.seasonRequest;
    const cached = this.seasonStatsCache.has(seasonStart);
    if (!cached) this.isLoading.set(true);
    try {
      const rows = await this.fetchSeasonStats(seasonStart);
      if (request !== this.seasonRequest) return;
      this.seasonClubStats.set(rows);
      this.rebuildClubStats();
    } catch (error) {
      console.error('Error loading selected club season:', error);
    } finally {
      if (request === this.seasonRequest) this.isLoading.set(false);
    }
  }

  private async fetchSeasonStats(seasonStart: number): Promise<ClubStatsRow[]> {
    const cached = this.seasonStatsCache.get(seasonStart);
    if (cached) return cached;
    const { data, error } = await this.supabase.client.rpc('get_club_season_stats', {
      p_start_date: seasonStartDate(seasonStart),
    });
    if (error) throw error;
    const rows = (data || []) as ClubStatsRow[];
    this.seasonStatsCache.set(seasonStart, rows);
    return rows;
  }

  private async loadAllStats(): Promise<void> {
    this.isLoading.set(true);
    try {
      const { data, error } = await this.supabase.client.rpc('get_club_stats');
      if (error) throw error;
      this.rawClubStats.set(data || []);
      this.allStatsLoaded.set(true);
      this.rebuildClubStats();
    } catch (error) {
      console.error('Error loading all-time club stats:', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  /** Clubs with at least one participation in the displayed period (season or all time). */
  private rebuildClubStats(): void {
    const directory = new Map(
      this.clubDirectory().map(club => [this.normalizeName(club.name), club]),
    );
    const rows = (this.statsScope() === 'all' ? this.rawClubStats() : this.seasonClubStats())
      .filter(row => Number(row['total_races']) > 0);
    const stats = rows
      .filter(row => row['club_name'] !== INDEPENDENTS && !this.isJunkClubName(row['club_name'] as string))
      .map(row => this.buildClubStats(row, directory));
    const independents = rows.find(row => row['club_name'] === INDEPENDENTS);
    if (independents) stats.push(this.buildClubStats(independents, directory));
    this.clubStats.set(stats);
  }

  private buildClubStats(row: ClubStatsRow, directory: Map<string, ClubDirectoryEntry>): ClubStats {
    const name = row['club_name'] as string;
    const directoryEntry = directory.get(this.normalizeName(name));
    const podiums = {
      gold: Number(row['gold']),
      silver: Number(row['silver']),
      bronze: Number(row['bronze']),
      chocolate: Number(row['chocolate']),
    };
    return {
      id: directoryEntry?.id,
      name: name === INDEPENDENTS ? this.translationService.translate('clubsKiosk.noClub') : name,
      isIndependents: name === INDEPENDENTS,
      region: directoryEntry?.region ?? 'Autre',
      logoUrl: directoryEntry?.logoUrl,
      backgroundImage: this.getClubBackground(name, directoryEntry?.region),
      cardColor: this.getRegionColor(directoryEntry?.region),
      // Count distinct humans with a result in the selected period, rather
      // than every profile attached to the club across all seasons.
      memberCount: Number(row['active_member_count']),
      activeMemberCount: Number(row['active_member_count']),
      totalDistance: Number(row['total_distance']),
      totalRaces: Number(row['total_races']),
      podiums: { ...podiums, total: podiums.gold + podiums.silver + podiums.bronze },
      topSpeed: 0,
    };
  }

  private async loadClubDirectory(): Promise<ClubDirectoryEntry[]> {
    try {
      return (await this.clubService.getClubs()).map(club => ({
        id: club.id,
        name: club.name,
        region: club.region,
        logoUrl: club.logoUrl,
      }));
    } catch (error) {
      // Logos, regions and ids are extras: the statistics are still shown without them.
      console.warn('Unable to load club metadata:', error);
      return [];
    }
  }

  private normalizeName(value: string | undefined): string {
    return (value || '').trim().toUpperCase();
  }

  // Scraper-created clubs sometimes hold parsing artefacts ('-', ': 1.6', '3').
  // They carry no usable identity, so hide them until the data is cleaned.
  private isJunkClubName(value: string | undefined): boolean {
    const name = (value || '').trim();
    if (name.length < 2) return true;
    if (!/[A-Za-zÀ-ÖØ-öø-ÿ]/.test(name)) return true;
    // Imported PDF columns sometimes append a distance or a dog's name.
    if (/\s\d+[,.]\d+$/.test(name)) return true;
    return new Set([
      'CANISPORTS CLUB DE',
      'CANISPORTS CLUB DE LA',
      'CANICROSS BRUSSELS SOUTHYUZU',
      'CLUB CANISPORT NORMANDPEPPER',
      'MUSHING CLUB DES FLANDRESUNA',
    ]).has(this.normalizeName(name));
  }

  private getRegionColor(region: string | undefined): string {
    return getFfslcRegionForAdministrativeRegion(region)?.color ?? '#2563EB';
  }

  private getClubBackground(name: string, region: string | undefined): string {
    const key = `${region ?? 'other'}:${name}`;
    let hash = 0;
    for (let index = 0; index < key.length; index++) {
      hash = (hash * 31 + key.charCodeAt(index)) | 0;
    }
    return this.clubBackgroundImages[Math.abs(hash) % this.clubBackgroundImages.length];
  }

}
